import React, { useState } from "react";
import { openMailTo } from "../../../utils/Common/contact";
import Button from "../../../Components/Common/Button/Button";
import type { DocLabel, DocState } from "./preconConfig.types";
import Input from "Components/Common/Input/Input";
import TickIcon from "Assets/Img/TickIcon";

const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
]);

const EXT_MIME_MAP: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

const ACCEPT = Object.keys(EXT_MIME_MAP)
  .map((ext) => `.${ext}`)
  .join(",");

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB

const FIELD_MIME_RESTRICTIONS: Record<
  string,
  {
    mimes: Set<string>;
    allowedExts?: Set<string>;
    accept: string;
    errorLabel: string;
  }
> = {
  architect_rendering_elevation: {
    mimes: new Set(["application/pdf", "image/jpeg"]),
    allowedExts: new Set(["pdf", "jpeg"]),
    accept: ".pdf,.jpeg",
    errorLabel: "Only PDF and JPEG files are allowed for this field.",
  },
};

const validateFile = (file: File, docKey?: string): string | null => {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mime = file.type || EXT_MIME_MAP[ext] || "";

  const restriction = docKey ? FIELD_MIME_RESTRICTIONS[docKey] : undefined;
  if (restriction) {
    if (!restriction.mimes.has(mime)) return restriction.errorLabel;
    if (restriction.allowedExts && !restriction.allowedExts.has(ext))
      return restriction.errorLabel;
    if (file.size > MAX_FILE_SIZE) return "File size cannot exceed 25 MB.";
    return null;
  }

  if (!ALLOWED_MIME_TYPES.has(mime) && !EXT_MIME_MAP[ext]) {
    return "Invalid file type. Only JPG, JPEG, PNG, PDF, DOC, DOCX, XLS, XLSX are allowed.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size cannot exceed 25 MB.";
  }
  return null;
};

interface DocumentPanelProps {
  docState: DocState;
  docLabels: DocLabel[];
  docFileNames: string[];
  onUpload: (n: number, file: File) => Promise<void>;
  onMarkNA: (n: number) => void;
  onRemove: (n: number) => void;
  docCount: number;
  totalDocs: number;
  localStorgeUserData: string;
}

const DocumentPanel = ({
  docState,
  docLabels,
  docFileNames,
  onUpload,
  onMarkNA,
  onRemove,
  docCount,
  totalDocs,
  localStorgeUserData,
}: DocumentPanelProps) => {
  const [resetKeys, setResetKeys] = useState<Record<number, number>>({});
  const [uploadingDocs, setUploadingDocs] = useState<Record<number, boolean>>(
    {},
  );
  const [uploadErrors, setUploadErrors] = useState<Record<number, string>>({});
  const clearError = (n: number) =>
    setUploadErrors((prev) => ({ ...prev, [n]: "" }));

  const handleRemove = (n: number) => {
    onRemove(n);
    clearError(n);
    setResetKeys((prev) => ({ ...prev, [n]: (prev[n] ?? 0) + 1 }));
  };

  const handleFileChange = async (n: number, file: File, docKey?: string) => {
    const validationError = validateFile(file, docKey);
    if (validationError) {
      setUploadErrors((prev) => ({ ...prev, [n]: validationError }));
      setResetKeys((prev) => ({ ...prev, [n]: (prev[n] ?? 0) + 1 }));
      return;
    }
    clearError(n);
    setUploadingDocs((prev) => ({ ...prev, [n]: true }));
    try {
      await onUpload(n, file);
    } finally {
      setUploadingDocs((prev) => ({ ...prev, [n]: false }));
    }
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="panel-head-title">Required Documents</div>
        <div className="panel-head-count">
          {docCount} / {totalDocs} submitted
        </div>
      </div>
      <div className="panel-body">
        {docLabels.map((doc, idx) => {
          const n = idx + 1;
          const state = docState[n];
          const isUploaded = state === "uploaded";
          const isNA = state === "na";

          const isUploading = !!uploadingDocs[n];
          const fieldAccept =
            FIELD_MIME_RESTRICTIONS[doc.key]?.accept ?? ACCEPT;

          return (
            <div key={n} className="upload-item">
              <div
                className={`ui-num ${isUploaded ? "done" : ""} ${isNA ? "na" : ""} ${isUploading ? "uploading" : ""}`}
              >
                {isUploading ? (
                  <span className="upload-spinner" />
                ) : isUploaded ? (
                  "✓"
                ) : isNA ? (
                  "—"
                ) : (
                  n
                )}
              </div>
              <div className="ui-body">
                <div className="ui-label">
                  {doc.label}
                  {doc.isCommercial && (
                    <span className="comm-only">Commercial</span>
                  )}
                  {doc.optional && <span className="opt">{doc.extra}</span>}
                </div>
                {!doc.optional && doc.extra && (
                  <span className="opt">{doc.extra}</span>
                )}
                {isUploading && (
                  <div className="ui-uploading-label">Uploading…</div>
                )}
                {isUploaded && !isUploading && (
                  <div className="ui-file-name visible">
                    <TickIcon />
                    {docFileNames[idx]}
                  </div>
                )}
                <div className="ui-actions">
                  {!isNA && (
                    <Input
                      key={resetKeys[n] ?? 0}
                      type="file"
                      name={`document-${n}`}
                      id={`document-${n}`}
                      label=""
                      accept={fieldAccept}
                      disabled={isUploading}
                      aria-label={
                        isUploaded
                          ? `Uploaded file: ${docFileNames[idx]}`
                          : "Upload File"
                      }
                      className={`upload-input ${isUploaded ? "uploaded" : ""} ${isUploading ? "disabled" : ""}`}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileChange(n, file, doc.key);
                      }}
                      icon={""}
                    />
                  )}
                  {isUploaded && !isUploading && (
                    <Button
                      className="remove-btn"
                      onClick={() => handleRemove(n)}
                    >
                      Remove
                    </Button>
                  )}
                  <Button
                    className={`na-btn ${isNA ? "active" : ""}`}
                    onClick={() => onMarkNA(n)}
                    disabled={isUploading}
                  >
                    {isNA ? "Marked N/A" : "N/A"}
                  </Button>
                </div>
                {uploadErrors[n] && (
                  <div className="upload-error">{uploadErrors[n]}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* <div className="panel-footer">
        <div className="footer-content">
          <strong>Using Dropbox, ProCore, or shared storage?</strong>
          <br />
          Add{" "}
          <a
            onClick={() => openMailTo(localStorgeUserData)}
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            {localStorgeUserData ?? "-"}
          </a>{" "}
          for access. If files are password protected, please provide the
          password in the relevant question field.
        </div>
      </div> */}
    </div>
  );
};

export default DocumentPanel;
